import dayjs from 'dayjs';
import moment from 'moment';
import utc from 'dayjs/plugin/utc.js';
import type { employee_performance_list } from '~/core/dashboard/dashboardSchema';
import type { ArrayNumber, ChargeDetails, TripDetail } from '~/core/Trip/tripSchema';
import type { EmployeeDetails } from '~/core/Login/loginSchema';
import crypto from 'crypto'
import { constants } from './constants';
import { useRef } from 'react';
import {  type FetcherWithComponents } from 'react-router';
import { employee } from './constants/lib/employee';
import { ActionErrorServer } from '~/core/util';
import { Effect, pipe } from 'effect';
import { callApi } from './common/hooks/axiosHelper';
const { OPEN , CLOSED , ALL } = employee.CLAIM.STATUS

export interface SuccessResponseData {
  [key: string]: string | number | boolean | object | null | undefined;
}

export type DeviceType = "mobile" | "tablet" | "desktop";

let _previousCookie = '';

type routePath =  'customer' | 'indent' | 'trucks' | 'trips'

type PageSearchParams = {
  offset?: string;
  currentPage?: string;
  limit?: string
};

interface clearCookieType {
  tab: string ; 
  path: routePath; 
  setState: Function ; 
  fetcher: FetcherWithComponents<any> ;
  cardcode?:string
}
interface pageType extends clearCookieType {
  pageSize : number
}
export interface SendWhatsAppMessageType {
  mobile: string,
  sourceName: string,
  destinationName: string,
  sourceCities?: Array<string>,
  destinationCities?: Array<string>,
  employee: EmployeeDetails,
  truck_type_code?: string,
  ton?: number
}

interface FormattedLocation {
  coordinates_formatted: string;
}

export type ApiError = {
  data?: {
    error?: string;
    message?: string | FiberFailure;
  };
  message?: string;
  response?: {
    data?: {
      error?: string;
      message?: string | FiberFailure;
      details?: {
        message?: string
      }
    };
  };
};

type FiberFailure = {
  _id?: string;
  cause?: {
    failure?: {
      message?: string;
      _tag?: string;
    };
  };
};

type Field<T> = {
  name: keyof T;
  value: unknown;
};

const now = dayjs()
const yyyymmdd = 'YYYY-MM-DD'
dayjs.extend(utc);

export const util = {
  offset: 0,
  limit: 50,
  today: dayjs().format(yyyymmdd),
  WEEK: now.subtract(7, 'day').format(yyyymmdd),
  MONTH: now.subtract(30, 'day').format(yyyymmdd),
  THREE_MONTH: now.subtract(90, 'day').format(yyyymmdd),
  isLoading: (fetcher: { state: string }) => {
    return fetcher.state === "loading" || fetcher.state === "submitting";
  },
  isEmpty: (value: unknown): boolean => {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (value && typeof value === "object") {
      return Object.keys(value).length === 0;
    }
    return true;
  },
  isPageChanging: (state: string) => {
    return ['loading', 'submitting'].includes(state)
  },
  separatorText: (values: Array<string | number | null | undefined>) => {
    const filteredValues = values.filter(value => value != null);
    return filteredValues.join(" | ");
  },
  copyText: (text: string) => {
    if (typeof window !== 'undefined') {
      window.navigator.clipboard.writeText(text);
    }
  },
  getTruckDetailText: (truck: {
    partner_name: string | null;
    truck_no: string;
    truck_type_code: string | null;
    truck_tat: number;
    primary_mobile_number: string | null;
    truck_city: string | null;
    truck_destinations: string | null;
  }) => {
    let text = `${truck.partner_name} \n`
    text += `${truck.truck_no} - ${truck.truck_type_code} - ${truck.truck_tat} hrs\n`
    text += truck.primary_mobile_number ? `O- ${truck.primary_mobile_number}\n` : ''
    text += `Source: ${truck.truck_city} \n`
    text += truck.truck_destinations ? `Destination: ${truck.truck_destinations} \n` : ''

    return text
  },
  getContentType: (filePath: string): string => {
    const extension = filePath?.split('.').pop()?.toLowerCase();

    const mimeTypes: Record<string, string> = {
      'pdf': 'application/pdf',
      'jpeg': 'image/jpeg',
      'jpg': 'image/jpeg',
      'png': 'image/png',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'xls': 'application/vnd.ms-excel',
      'xl': 'application/vnd.ms-excel',
      'mp3': 'audio/mpeg',
      'wav': 'audio/wav',
      'ogg': 'audio/ogg',
    };

    return mimeTypes[extension || ''] || 'application/octet-stream';
  },
  calculate_tat_hours: (created_at: Date) => {
    if (!created_at) return "0h";

    const created_time = moment(created_at, "DD-MM-YYYY HH:mm:ss");

    if (!created_time.isValid()) return "0h";

    const now = moment();
    const diffHours = now.diff(created_time, "hours");

    if (diffHours < 24) {
      return `${diffHours}h`;
    }

    const diffDays = now.diff(created_time, "days");
    return `${diffDays}d`;  // Switch to "days" if more than 24 hours
  },
  formatTimeAgo: (timestamp: string) => {
    const diffMs = Date.now() - new Date(timestamp).getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    if (diffMinutes < 60) {
      return `${diffMinutes} minutes ago`;
    }
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      return `${diffHours} hours ago`;
    }
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} days ago`;
  },
  formatLabelBasedOnParam: (str: string) => {
    return (str.charAt(0).toUpperCase() + str.slice(1)).split('_').join(' ')
  },
  setCookie: (cookieName: string, cookieValue: string, exdays: number) => {
    const d = new Date();
    d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
    const expires = "expires=" + d.toUTCString();
    document.cookie = `${cookieName}=${cookieValue};${expires};path=/`;
  },
  clearCookie : (cookieName: string) => {
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  },
  getCookie(cookieString: string, name: string): string | Record<string, unknown> | undefined {
    const cookies = cookieString.split(';').map((cookie) => cookie.trim());
    const cookieObj: Record<string, string | Record<string, unknown>> = {};

    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.split('=');
      if (!cookieValue) continue;

      const decodedValue = decodeURIComponent(cookieValue);
      if (cookieName === '_book' && !cookieString.includes("customer")) {
        const parts = decodedValue.split('.');
        try {
          const payload = JSON.parse(atob(parts[0]));
          cookieObj["customer"] = payload?.user;
        } catch {
          console.warn("Failed to decode '_book' cookie value");
        }
      } else if (cookieName === name) {
        try {
          cookieObj[name] = JSON.parse(decodedValue);
        } catch {
          cookieObj[name] = decodedValue;
        }
      }
    }
    return cookieObj[name];
  },
  getLatLng: (location: string): [number, number] | null => {
    if (!location || location.length === 0) return null;

    const splitLocation = location.split(',');
    if (splitLocation.length !== 2) return null;

    const long = parseFloat(splitLocation[0].replace('(', ''));
    const lati = parseFloat(splitLocation[1].replace(')', ''));

    return [long, lati];
  },

  getDifferenceInHours: (date1: string | Date, date2: string | Date): number => {
    const current = new Date(date1).valueOf();
    const previous = new Date(date2).valueOf();

    const diff = current - previous;
    const difference_in_hours = Math.floor(diff / 3600000);

    return difference_in_hours;
  },

  is_roles: (allowed_roles: Array<string>, employee_role_names: Array<string>) => {
    const result = employee_role_names.some((role: string) => allowed_roles.includes(role))
    return result
  },
  is_employee_id_matched:(ids:{[key:string]:any},employeeId:number)=>{
    for(let id in ids){
     if(ids[id]===employeeId)return true
    }
    return false
  },
  isObjectEmpty: (object: Record<string, unknown>) => {
    return Object.keys(object || {}).length === 0;
  },
  checkValuesUpdated: (checkList: Array<string>, afterEvent: { [key: string]: any }, beforeEvent: { [key: string]: any }): boolean => {

    let loopLength = 0
    const listLength = checkList?.length

    if (afterEvent == null || beforeEvent == null) return false

    for (const key of checkList) {
      const afterValue = key in afterEvent ? afterEvent[key] : null

      const beforeValue = key in beforeEvent ? beforeEvent[key] : null

      if (afterValue !== beforeValue) break;

      loopLength += 1
    }

    return loopLength !== listLength

  },
  checkArraysAreEqual: (array1: ArrayNumber, array2: ArrayNumber) => {
    const firstLen = array1?.length
    const secondLen = array2?.length

    if (firstLen !== secondLen) return false

    const map = new Map()
    let count = 0

    for (let i = 0; i < firstLen; i++) {
      const value = array1[i]
      count = map.get(value) || 0
      count++
      map.set(value, count)
    }

    for (let i = 0; i < firstLen; i++) {
      const value = array2[i]
      count = map.get(value)
      const isValueNotExist = !map.has(value)
      const isCountNotValidate = count == 0

      if (isValueNotExist || isCountNotValidate) return false

      --count
      map.set(value, count)
    }

    return true
  },
  /** Function to encrypt or decrypt a given value
  * @param value - The value to encrypt or decrypt
  * @param type - The type of operation to perform. Must be either "ENCRYPT" or "DECRYPT"
  * @returns The encrypted or decrypted value, or null if there is an error
  */
  encryptAndDecrypt: (value: any = '', type: 'ENCRYPT' | 'DECRYPT') => {
    const isNode = typeof Buffer !== 'undefined';

    if (type !== 'ENCRYPT' && type !== 'DECRYPT') {
      console.error('Invalid encryption type');
      return null;
    }

    switch (type) {
      case 'ENCRYPT': {
        // Convert non-string values to JSON before encoding
        const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
        return isNode ? Buffer.from(stringValue).toString('base64') : btoa(stringValue);
      }

      case 'DECRYPT': {
        if (typeof value !== 'string' || !/^[a-zA-Z0-9+/]*={0,2}$/.test(value)) {
          console.error('Invalid Base64 format');
          return null;
        }
        try {
          const decoded = isNode ? Buffer.from(value, 'base64').toString('utf8') : atob(value);
          const parsed = JSON.parse(decoded);
          return parsed; // Returns parsed array/object if JSON, otherwise raw string
        } catch (error) {
          console.error('Decryption error: ', error);
          return value; // Return original value if parsing fails
        }
      }

      default:
        return null;
    }
  },
  getDevicePlatformUsingUserAgent: (userAgent: string) => {
    const platform = /android/i.test(userAgent)
      ? "android"
      : /iPhone|iPad|iPod/i.test(userAgent)
        ? "ios"
        : "web";

 return platform
  },
  trucncate: (data: string, length: number) => data && data.length > length ? data.slice(0, length) + '...' : data,
  getTime(no_minutes: boolean) {
    const d = new Date();
    const minutes = Math.round(d.getMinutes() / 15) * 15;
    return { hours: d.getHours(), minutes: no_minutes ? 0 : minutes === 60 ? 0 : minutes }
  },
  utcTimeString(date: string) {
    return dayjs.utc(date).format('DD-MM-YYYY HH:mm')
  },
  now: () => dayjs.utc().format('YYYY-MM-DDTHH:mm:ss'),
  currentTime() {
    return new Date().toISOString()
  },
  currentDate() {
    const now = new Date();
    const offsetMs = 5.5 * 60 * 60 * 1000; // 5h 30m in ms
    const ist = new Date(now.getTime() + offsetMs);
    return ist.toISOString().replace("Z", "+05:30");
  },
   sumBy <T>(array: T[], key: keyof T): number {
    return array.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);
},
validateDecimalFormat : (value: any) => {
  const decimalRegex = /^(\d+)(\.\d{2})?$/; // Regex pattern for two decimal places
  return decimalRegex.test(value);
},
validateAddress(address: string): boolean {
  const addressRegex: RegExp = /^[a-zA-Z0-9\s,'-_&@;]*$/;
  return addressRegex.test(address);
},
validateName: (name: string) => {
 //This validation accepts dot , &  to be used in names.
const format = /[!@#$%^*()_+\-=[\]{};':"\\|,<>/?]+/;
const matched_text = format.test(name)
 return matched_text
},
validate_special_characters_and_number: (name: string) => {
    const format = /[!@#$%^&*()_+\-=0-9\[\]{};':"\\|,.<>\/?]+/;
    let matched_text = format.test(name)
    return matched_text
  },
validate_special_characters_and_letter: (value: string) => {
    const format = /[!@#$%^&*()_+\-=a-zA-Z\[\]{};':"\\|,.<>\/?]+/;
    return format.test(value)
  },
validate_special_characters: (value: string) => {
    const format = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;
    return format.test(value)
  },
validateNonFr8Mail: (email:string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
  return emailRegex.test(email) && !email.endsWith("@fr8.in");
},
validateMobile: (mobile: string) => {
  return /^[0-9]{10}$/.test(mobile)
},
validatePan: (pan_number: string): boolean => {
     const pan = pan_number.replace(/\s/g, "").toUpperCase();
    const regpan = /^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}$/;
    return pan.length === 10 ? regpan.test(pan) : false;
 },
 validateGst: (gst_number: string): boolean => {
    const gst = gst_number.replace(/\s/g, "").toUpperCase();
    const regpan = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[A-Z]{1}[0-9A-Z]{1}$/;
    return gst.length === 15 ? regpan.test(gst) : false;
},
 validatePincode: (pincode: string): boolean  => /^[0-9]{6}$/.test(pincode),
 groupBy<T, K extends keyof T>(array: T[], key: K): Record<T[K] & string, T[]> {
  return array?.reduce((result, item) => {
    const groupKey = item[key] as string;
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {} as Record<string, T[]>);
},
getChargeAmount: (invoice_charge:ChargeDetails[], type: number, requested = true) => {
  const charge = invoice_charge.find((charge: { charge_type_id:number | null }) => charge.charge_type_id === type)
  return charge ? (requested ? charge.partner_requested_amount : charge.partner_approved_amount) : null
},
  getListWithRank: (record: employee_performance_list, employee_id: number) => {
    const array: string | any[] = [];
    let overall_rank: number = 0
    let rank = 1;
    for (let i = 0; i < record?.length; i++) {
      const j1 = record[i]?.current_week_count || 0
      const j2 = record[i - 1]?.current_week_count || 0
      if (i > 0 && j1 < j2) {
        rank++;
      }
      if ((record[i].employee_id) === employee_id) {
        overall_rank = rank;
      }

      array.push({ ...record[i], rank: rank });
    }
    return { array, overall_rank };
  },
  clickToCall: (mobileNo: string): void => { window.location.href = 'tel:+91' + mobileNo; },
  formatDate: (date: string, format?: string) => {
    return date ? moment(date).format(format ||  "DD-MMM HH:mm" ) : "-";
  },
  percentageCalculator: (total: number, value: number) => {
    let percent = 0
    const traveled = total - value
    if (traveled < total && traveled > 0) {
      percent = (traveled / total) * 100
    } else if (traveled < 1) {
      percent = 1
    } else {
      percent = 100
    }
    return percent
  },
  mathCalc: (v1: string, v2: string, type: 'sum' | 'subtract' | 'multiplay' | 'divide' | 'percentage'): string | undefined => { // Use only if value came from form field
    const value1 = (!v1 || v1 === '') ? 0 : +v1
    const value2 = (!v2 || v2 === '') ? 0 : +v2

    if (type === 'sum') { return (value1 + value2).toString() }
    if (type === 'subtract') {
      const result = (value1 - value2)
      return result < 1 ? '0' : result.toString() // negative value changing to 0
    }
    if (type === 'multiplay') {
      return (value1 * (value2 || 1)).toString()
    }
    if (type === 'percentage') {
      return (!value1) ? '0' : Math.ceil((value1 * value2) / 100).toString()
    }
    if (type === 'divide') {
      return (value1 && value2) ? (value1 / value2).toString() : '0'
    }
  },
  findCreatedBy(updated_by: String) {
    //This function returns true if created by partner is true
    const created_by = updated_by != null && updated_by != " " ? updated_by.split('@') : null
    const get_created_by = created_by ? created_by[0] : null
    const check = get_created_by ? /^\d+$/.test(get_created_by) : false
    return check
  },
  onShare: async (formatted_msg:any, mobile?:string | null) => {
    let url
    if(!mobile){
      url = `https://api.whatsapp.com/send?text=${encodeURI(formatted_msg)}&app_absent=0`
    }
    else if(window.innerWidth < 720){
     url = `https://api.whatsapp.com/send?phone=+91${mobile}&text=${encodeURI(formatted_msg)}&app_absent=0`;
    }else {
      url = `https://web.whatsapp.com/send?phone=+91${mobile}`;
      url += `&text=${encodeURI(formatted_msg)}&app_absent=0`;
    }
    window.open(url);
  },
  sendWhatsAppMessage: (input: SendWhatsAppMessageType) => {
    const { employee, mobile, truck_type_code, ton, sourceCities=[], destinationCities=[], destinationName, sourceName } = input;
    const employeeName = employee?.employee_name
    const branchName = employee?.branch_and_team_detail?.branch[0].branch_name
    const truckTypeTonMsg = truck_type_code && ton ? `-${truck_type_code}, ${ton} ton` : truck_type_code ? `-${truck_type_code}` : ton? `-${ton} ton`:''
    const loadingPoints= sourceCities.length > 0 && sourceCities?.join(' ');
    const unLoadingPoints= destinationCities.length > 0 && destinationCities?.join(' ');
  
    const message = `Hi, i am ${employeeName} from FR8 ${branchName} branch.\n${sourceName} to ${destinationName}${truckTypeTonMsg} load available!.
    \n ${sourceCities.length ? sourceCities.length : '1'} point loading and ${destinationCities.length ? destinationCities.length : '1'} point unloading.
    \n ${loadingPoints} \n ${unLoadingPoints}`
    util.onShare(message,mobile)
  },
  getDeviceTypeFromWidth(width: number): DeviceType {
  if (width <= 767) return "mobile";
  if (width <= 1024) return "tablet";
  return "desktop";
  },
  getDeviceTypeFromUserAgent(userAgent: string): DeviceType {
    const ua = userAgent.toLowerCase();    

    if (/mobile|iphone|ipod|android.*mobile|blackberry|nokia/.test(ua)) {
      return "mobile";
    }
  
    if (/ipad|tablet|android(?!.*mobile)/.test(ua)) {
      return "tablet";
    }
  
    return "desktop";
  },
  deviceTypeToCodeMap: {
    mobile: 1,
    desktop: 2, 
    tablet: 3,
  },
  encryptWithExp:(encrypting_text: string) => {
    const ENC_KEY = constants.CHAT_ENC_KEY
    const IV_HEX = constants.CHAT_IV_HEX
    const IV = Buffer.from(IV_HEX, 'hex'); // 16-byte IV

    try {
        const key = Buffer.from(ENC_KEY, 'hex'); // ✅ Now key is 32 bytes (for AES-256)
        const cipher = crypto.createCipheriv('aes-256-cbc', key, IV);
        const withexpDate=JSON.stringify({ data: encrypting_text, exp: Date.now() + 60 * 1000 })
        let encrypted = cipher.update(withexpDate, 'utf8', 'base64');
        encrypted += cipher.final('base64');
        return encrypted;
    } catch (error) {
        throw new Error(`Error while encrypting: ${error}`);
    }
  },
  composeRatesParam: (route: any, truckGroup: any) => {
    return `s_id=${route.sourceId}&b_id=${route.sourceBranchId}&d_id=${route.destinationId}&db_id=${route.destinationBranchId}&tg_id=${truckGroup.primary_group_id}&from=${route.sourceName}&to=${route.destinationName}`
  },
  getPastNthDate:(days:number) => {
    const currentDate = new Date();
    const pastDate = new Date(currentDate);
    pastDate.setDate(currentDate.getDate() - days);
    return pastDate;
  },
  isMobileRegex: new RegExp(
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i,
  ),
  getView(keyName: string) {
    const view = localStorage.getItem(keyName) ||
      (util.isMobileRegex.test(navigator.userAgent) ? "card" : "table")
    return view
  },
  useDebouncedEffect:<A>(fn: (a: A) => void, delay = 500) => {
    const ref = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
    return (value: A) => {
      if (ref.current) clearTimeout(ref.current)
      ref.current = setTimeout(() => {
        fn(value)
      }, delay)
    }
  },
  employeeCookie(employee:EmployeeDetails) {
      return {
        employeeId:employee.employee_id,
        employeeName:employee.employee_name,
        createdBy:employee.email,
        authUserId:employee.auth_user_id,
        growLandingPage: employee.grow_landing_page,
        employeeAccessNames: employee.employee_access_names
      }
  },
  deepGet(
    obj: Record<string, any>,
    path: string,
    defaultValue: string = ""
  ): string {
    const result = path.split('.').reduce((acc: unknown, key: string) => {
      if (acc && typeof acc === 'object' && acc !== null && key in acc) {
        return (acc as Record<string, unknown>)[key];
      }
      return undefined;
    }, obj);
  
    return typeof result === "string" ? result : defaultValue;
  },
  parseObject(obj: Record<string, any>) {
    const result: Record<string, any> = {};
    for (const key in obj) {
      try {
        result[key] = JSON.parse(obj[key]);
      } catch(e) {          
        result[key] = obj[key];
      }
    }
    return result;
  },
  filters:{
    last_load_data_customer_support:[
        {
            id: 1,
            name: 'Last month'
        },
        {
            id: 2,
            name: 'Last two month'
        },
        {
            id: 3,
            name: 'Custom range'
        }
      ],
    trip_count_data_customer_support:[
        {
            id: 1,
            name: '0 trip'
        },
        {
            id: 2,
            name: '1-3 trip'
        },
        {
            id: 3,
            name: 'More than 3 trip'
        }
      ]
  },
    hasCookieChanged(): boolean {
      if (typeof document === 'undefined') return false;
    
      const current = document.cookie;
      if (current !== _previousCookie) {
        _previousCookie = current;
        return true;
      }
    
      return false;
    },
    isWalletNegative: (wallet: number | null) => {
    return wallet !== null && wallet < 0
  },

    handleRoute : (path:routePath,cardcode:string,params:PageSearchParams ,tab:string) => {
    const searchParams = new URLSearchParams(params).toString();
    const isWatchingForLoad = tab == 'waitingForLoad'
    const routeMap: Partial<Record<routePath, string>> = {
      indent: `/employee/${path}?${searchParams}`,
      customer: `/employee/${path}/${cardcode}/${tab}?${searchParams}`,
      trucks : isWatchingForLoad ? `/employee/${path}?${searchParams}` :`/employee/${path}/${tab}?${searchParams}`
    };
    return routeMap[path] ?? `/employee/${path}/${tab}?${searchParams}`;
  },

  handlePageChange : ( props: pageType ) => {
    const { pageSize, setState, tab , path , fetcher , cardcode='' } = props
    const newOffset = (pageSize - 1) * util.limit;
    setState((prevState) => ({...prevState , offset : newOffset , currentPage : pageSize}))
    const params = { offset: newOffset.toString(), currentPage: pageSize.toString()}
    util.setCookie(tab, JSON.stringify(params) , 1)
    const route = util.handleRoute(path,cardcode, params , tab)
    fetcher.load(route)
  },

  handleClearCookie : (props: clearCookieType ) => {
    const { tab , path , setState , fetcher, cardcode='' } = props
    const cookieData = util.getCookie(document?.cookie, tab);
     if (cookieData && typeof cookieData === 'object' && 'offset' in cookieData) {
      const params = { offset: String(cookieData.offset) , limit : String(cookieData?.limit ?? 50 ) }
      setState((prevState) => ({...prevState , offset : Number(cookieData.offset) , currentPage : Number(cookieData.currentPage)}))
      const route = util.handleRoute(path, cardcode, params , tab)
      fetcher.load(route)
    }
  },
  validateUrl: (url: string) => {
    const urlFormat = /^(https?:\/\/)([\w.-]+)(\.[\w.-]+)+/;
    return urlFormat.test(url);
  },
  messageFormat: (data: any) => {
  const lines = [
    data.line1,
    data.line2,
    data.line3,
    data.line4,
    data.line5,
    data.line6, // will be skipped if undefined
    data.line7,
  ].filter(item => item != undefined); // removes undefined/null/false

  const message = `${lines.join('\n')}\n\nCheers,\nTeam FR8\n${data.footer}`;
  return message;
},
  //for invoice message to customer
  invoiceMessageFormat: (data: any) => {
    const message = `Trip Invoiced!! 👍\n
${data.line1}
${data.line2}
${data.line3}\n
Cheers,
Payment Team FR8
${data.footer}`;
    return message;
  },

  getCurrentAndPreviousDatesInMonth : (employee_joined_at: string) => {
    const allDatesInMonth: string[] = [];
    const currentDate = moment();
    const joiningDate = moment(employee_joined_at);
    const isWithinLast31Days = joiningDate.isAfter(currentDate.clone().subtract(31, 'days'));
  
    // Determine the start date based on the conditions
    const getStartDate = () => {
      if (isWithinLast31Days) return joiningDate.clone();
      return currentDate.clone().startOf('month').subtract(1, 'month').date(27); 
    };
  
    let initialDate = getStartDate();
  
    while (initialDate.isBefore(currentDate) || initialDate.isSame(currentDate, 'day')) {
      if (initialDate.day() !== 0) {
        allDatesInMonth.push(initialDate.format('YYYY-MM-DD'));
      }
      initialDate.add(1, 'day');
    }
    return allDatesInMonth
},
getCurrentMonthYear : () => {
      const year_actual = parseInt(dayjs().format('YYYY'))
      const month_actual = parseInt(dayjs().format('M'))
      const previous_month = parseInt(dayjs().set('month', month_actual - 1).subtract(1, 'month').format('M'));
      const year : [number]  =   [year_actual]
      const month : [number, number] = [previous_month , month_actual] 
      return { year , month}
},
getClaimListTextColorAndName: (expense_status_id: number) => {
  return (employee.CLAIM.CLAIM_LIST_STATUS.find(status => status.id === expense_status_id) || { name: '-', color: 'yellow' });
},
//TODO need to do role level access in authorization table after JWT is implemented for both is_access_included and isRoleAllowed
is_access_included: (allowed_roles: Array<{ role_id: number }>, context: Array<number>) =>
    allowed_roles.length > 0
      ? context.some(role => allowed_roles.map(r => r.role_id).includes(role))
      : false,

  rolePermissions: {
    under10000: ['RM', 'ZM', 'Admin'],
    from10000To25000: ['ZM', 'Admin'],
  },

  isRoleAllowed: (amount: number, roles: string[]): boolean => {
    const roleValidationMap = [
      { maxAmount: 10000, validRoles: util.rolePermissions.under10000 },
      { maxAmount: 25000, validRoles: util.rolePermissions.from10000To25000 }
    ];
    return roleValidationMap.some(
      ({ maxAmount, validRoles }) =>
        amount <= maxAmount &&
        roles.some(role => validRoles.includes(role))
    );
  },

  handleApiError: (error: ApiError) => {
     const messageObj = error?.response?.data?.message;
    const nestedMessage =
      typeof messageObj === "string"
        ? messageObj
        : messageObj?.cause?.failure?.message;

    const message =
    error?.response?.data?.details?.message || 
      nestedMessage ||
      error?.response?.data?.error ||
      error?.message ||
      "An unknown error occurred";
     return ActionErrorServer.make({
      message,
      event_at: new Date().toISOString(),
      _tag: "ACTION_ERROR_SERVER"
    });
  },
  setIssueType: <T extends Record<string, unknown>>(
    value: { id: number; name?: string; partner?: boolean; customer?: boolean },
    watch: (name: keyof T) => unknown,
  ): Array<Field<T>> => {
    // Cast the watched value to a specific object type before spreading it.
    const watchedIssueType = watch("issue_type" as keyof T) as Record<string, unknown>;

    const _data: Array<Field<T>> = [
      { name: "issue_type_name" as keyof T, value: value?.name },
      {
        name: "issue_type" as keyof T,
        value: { ...watchedIssueType, id: value?.id, name: value?.name, partner: value?.partner, customer: value?.customer },
      },
    ];
    return _data;
  },
  onCopyPaste :(e: { preventDefault: () => void }) => {
    e.preventDefault();
    return false;
  },
  getClaimStatusId : (status: { Open?: boolean; Closed?: boolean }): Array<number> => {
  const statusMap: Record<string, Array<number>> = {
    "true|false": OPEN,    
    "false|true": CLOSED,  
    "true|true": ALL,     
    "false|false": ALL,  
  };

  const key = `${status?.Open === true}|${status?.Closed === true}`;

  return statusMap[key] ?? ALL;
},
getResignationCardTextColor: (status_id:number) => {
    return employee.RESIGNATION.STATUS[status_id as keyof typeof employee.RESIGNATION.STATUS] || ""
  },

  swapLatLng: (coordinates_string: string): string => {
    const cleaned_string = coordinates_string.replace("(", "").replace(")", "");
    const formatting_array = util.getLatLong(cleaned_string);
    return formatting_array
      ? `${formatting_array[1]},${formatting_array[0]}`
      : (() => {
        throw new Error("Invalid coordinates string");
      })();
  },
  parseLocation: (location: string | undefined | null): [number, number] | null => {
    const match = location?.match(/\(([^,]+),([^)]+)\)/);
    if (!match) return null;
    const lat = Number(match[1]);
    const lng = Number(match[2]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
  },

  getLatLong: (location: string): [number, number] => {
    const path = location
      .trim()
      .split(/[,\s.]+/)
      .map((v) => parseFloat(v))
      .filter((v) => !isNaN(v));

    return path.length >= 4
      ? ([`${path[0]}.${path[1]}`, `${path[2]}.${path[3]}`].map((v) =>
        parseFloat(v)
      ) as [number, number])
      : (() => {
        throw new Error("Invalid location format");
      })();
  },
  getformattedLatLongFromLocation: (
    location: [number, number] | number[] | string
  ): FormattedLocation => {
    const strCurrentCoordinate =
      Array.isArray(location) && location.length >= 2
        ? [location[0], location[1]]
        : typeof location === "string"
          ? util.getLatLong(location)
          : (() => {
            throw new Error("Invalid manual location format");
          })();

    return {
      coordinates_formatted: `(${strCurrentCoordinate[0]},${strCurrentCoordinate[1]})`,
    };
  },

  getCalculatedDistance: (
    from: string,
    to: string,
    unit: "K" | "N" | "M" = "K"
  ): number => {
    const [lat1, lon1] = util.getLatLong(from);
    const [lat2, lon2] = util.getLatLong(to);

    return lat1 === lat2 && lon1 === lon2
      ? 0
      : (() => {
        const radlat1 = (Math.PI * lat1) / 180;
        const radlat2 = (Math.PI * lat2) / 180;
        const theta = lon1 - lon2;
        const radtheta = (Math.PI * theta) / 180;
        const rawDist =
          Math.sin(radlat1) * Math.sin(radlat2) +
          Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);

        const distMiles = Math.acos(rawDist > 1 ? 1 : rawDist) * (180 / Math.PI) * 60 * 1.1515;
        const unitConversion: Record<"K" | "N" | "M", number> = {
          K: 1.609344, // kilometers
          N: 0.8684,   // nautical miles
          M: 1,        // miles (default)
        };

        return distMiles * (unitConversion[unit] ?? 1);
      })();
  },
  calculateEta: (datastoCalculateETA: {
    location_at: string | null;
    remaining_km: number | null;
    avg_km: number | null;
  }): string => {
    const { location_at, remaining_km, avg_km } = datastoCalculateETA;
    const currentLocation = location_at && moment(location_at).isValid() && moment(location_at).isAfter(moment())
      ? moment(location_at)
      : moment();

    const average_km = avg_km ? avg_km : 500;
    const remainingKm = remaining_km ?? 0;

    const millisecondsToAdd = (remainingKm * 1000 * 3600) / (average_km / 24);

    const eta = moment(currentLocation)
      .add(millisecondsToAdd, "milliseconds")
      .format("YYYY-MM-DDTHH:mm:ss");

    console.log("✅ FINAL ETA:", eta);
    return eta;
  },
   isHumanCheck : (humanKey: string) => {
    return pipe(
      Effect.fromNullable(humanKey),
      Effect.catchAll(() => Effect.fail(new Error("humanKey is required."))),
      Effect.flatMap((key) =>
        pipe(
          Effect.tryPromise(() =>
            fetch(constants.RECAPTCHA_VERIFY_URL, {
              method: "POST",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/x-www-form-urlencoded; charset=utf-8",
              },
              body: `secret=${import.meta.env.VITE_CAPTCHA_SERVER_KEY}&response=${key}`,
            })
          ),
          Effect.catchAll(err => Effect.fail(new Error(`Error in Google Siteverify API. ${err.message}`)))
        )
      ),
  
      Effect.flatMap((response) => {
        return response.ok
          ? Effect.succeed(response)
          : Effect.fail(new Error(`Error in Google Siteverify API. Status: ${response.status}`))
      }
      ),
  
      Effect.flatMap(response => {
        return pipe(
          Effect.tryPromise(async () => {
            const text = await response.text();
            return JSON.parse(text);
          }),
          Effect.catchAll(err =>
            Effect.fail(new Error(`Failed to parse response JSON. ${err.message}`))
          )
        )
      }),
  
      Effect.flatMap(result => {
        return result.success
          ? Effect.succeed(result)
          : Effect.fail(new Error("YOU ARE NOT A HUMAN."))
      }
      )
    )
  },
  getEmployeeSession:() => {
    const employeeSession = localStorage.getItem(constants.EMPLOYEE_SESSION);
    if (!employeeSession) return {} as EmployeeDetails;
    const parsed = JSON.parse(employeeSession);
    // Guard: if a { _tag, employee } wrapper leaked into storage from a
    // transient mid-migration state, unwrap it so callers always see a flat employee.
    const employee: EmployeeDetails =
      parsed && parsed._tag && parsed.employee ? parsed.employee : parsed;
    return employee;
  },
  deleteFile: (arg:any) => {
    return Effect.tryPromise({
      try: () => {
        return callApi({
          apiClientKey: "help",
          method: "post",
          url: "/employee/file-delete",
          data:arg.body
        }).then((res) => res.data);
      },
      catch: (error: any) => new Error(util.handleApiError(error).message),
    });
  },
  isEmployeeAllowedAccess : (
    employee_id: number,
    amount: number,
    team_manager: number,
    zone_manager: number,
    team_lead_id: number
  ): boolean => {
    let allowed_roles: number[] = [];
 
    if (amount > 0 && amount <= 5000) {
       allowed_roles = [team_manager, team_lead_id, zone_manager];
    } else if (amount > 5000 && amount <= 25000) {
      allowed_roles = [team_manager, zone_manager];
    } else if (amount > 25000) {
      allowed_roles = [zone_manager];
    }
  
    return allowed_roles.includes(employee_id);
  },
  getSourceDestination:(city:string | null | undefined, state: string | null) => {
    return city ? city && state ? `${city}, ${state}` : city  : ''
  },
  getTopayPaymentLinkMessage: (link: string, trip_info: TripDetail) => {
     const trip_id = trip_info?.id
    const source = trip_info.source
    const destination = trip_info.destination
    const truck_no = trip_info.truck_no
    const trip_commission_amount = trip_info.trip_commission_amount

    let message = `#${trip_id} ${source} to ${destination} ${truck_no} \n`
    message += `Hiring Charge:  ₹${trip_commission_amount} \n`
    message += `${source} ${destination} is due for Confirmation. \n`,
    message += `Click here to pay now:${link} \n`
    return message
  },
  getCustomerVirtualAccountDetails:(customerName:string | null, virtualAccount:string | null, accountDetails: {branch: string, ifscCode: string, accountHolderName: string}) => {
    return `Hi ${customerName},\nPlease find the payment details below:\n🏦 Bank : ICICI, \n📍 Branch : ${accountDetails.branch}, \n🔢 Virtual Account Number : ${virtualAccount}, \n👤 Account Holder Name : ${accountDetails.accountHolderName},\n📄 IFSC Code : ${accountDetails.ifscCode} \n\n✅ Note : This virtual account is assigned exclusively to your transaction, ensuring seamless payment tracking.`
  },
  getPaymentLinkMessage: (link: string, trip_info: TripDetail, amount: number) => {
    const trip_id = trip_info?.id
    const source = trip_info?.source
    const destination = trip_info?.destination
    const truck_no = trip_info?.truck_no
    const source_out = trip_info?.source_out

    let message = `#${trip_id} ${source} to ${destination} ${truck_no} \n`
    message += `Loaded On : ${source_out ? moment(source_out).format("DD-MMM-YYYY") : ""} \n`
    message += `Hire Advance: ₹${amount} \n`
    message += `Payment Link: ${link} \n`
    message += '\n'
    message += `Cheers,\n`
    message += 'Team FR8'
    return message
  }
};


